<script lang="ts">
import "@cisco-u/tags/cu-tags.js";

import dayjs from "dayjs";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";

const retiringSoonTxt = "Retiring soon";

export default defineComponent({
  props: {
    content: {
      type: Object,
      required: true,
    },
    tags: {
      type: Array<string>,
      default: [],
    },
    isRevup: {
      type: Boolean,
      default: false,
    },
    priceDetails: {
      type: Object,
      default: () => {},
    },
    showRevUpRecertCampaign: {
      type: Boolean,
      default: false,
    },
    isStorePage: {
      type: Boolean,
      default: false,
    },
    isLabPage: {
      type: Boolean,
      default: false,
    },
    showSubscriptionTag: {
      type: Boolean,
      default: false,
    },
    inGracePeriod: {
      type: Boolean,
      default: false,
    },
    squared: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      freeId: "content/freeId",
      userEntitlements: "user/entitlements",
      popularProduct: "pricing/popularProduct",
    }),
    freeContent(): boolean {
      const isInContentBundle = this.content?.content_bundle?.includes(
        this.freeId
      );

      const isInGlobalProducts = this.content?.global_products?.some(
        (product: any) => {
          return product.content_bundle_id === this.freeId;
        }
      );

      return isInContentBundle || isInGlobalProducts;
    },
    newContent(): boolean {
      if (this.content?.type === "challenge") {
        const globalProduct = this.content?.global_products?.find(
          (product: any) => product?.is_single === 1
        );
        if (!globalProduct?.ga_date && !this.content?.ga_date) return false;
        const ga_date = new Date(
          globalProduct?.ga_date ?? this.content?.ga_date
        );
        ga_date.setDate(ga_date.getDate() + 30);
        return new Date() < ga_date;
      }
      if (!this.content?.created_at) return false;
      const created_at = new Date(this.content?.created_at);
      created_at.setDate(created_at.getDate() + 30);
      return new Date() < created_at;
    },
    isRetiringSoon(): boolean {
      if (!this.content) return false;

      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const currentDateObj = new Date();

      // Determine the correct eos_date to use
      const globalProduct = this.content?.global_products?.find(
        (product: any) => product?.is_single === 1
      );
      const eosDateStr = globalProduct?.eos_date ?? this.content?.eos_date;

      if (!eosDateStr) return false;

      const eosDateObj = new Date(eosDateStr);

      // Check if the eosDateObj is a valid date
      if (isNaN(eosDateObj.getTime())) {
        console.error("Invalid EOS date provided.");
        return false;
      }

      // Calculate the difference in days
      const differenceInDays = Math.ceil(
        (eosDateObj.getTime() - currentDateObj.getTime()) / MS_PER_DAY
      );

      // Check if the current date is within 30 days of the EOS date
      return differenceInDays >= 0 && differenceInDays <= 30;
    },
    contentRetired(): string {
      if (!this.content) return "";
      if (
        this.content?.type === "path" ||
        this.content?.lab_type == "graded-labs"
      ) {
        switch (this.content?.content_retired) {
          case "RETIRING_SOON":
            return "Retiring soon";
          case "RETIRED":
            return "Retired";
          default:
            return "";
        }
      }
      return "";
    },
    results(): Array<string> {
      let data = this.content?.tags ?? [];
      data = Array.isArray(data) ? data : [data];

      data = data?.concat(this.tags.filter((tag) => !data.includes(tag)));
      data = data ?? [];

      // check if content is free, and add appropriate tags
      if (this.freeContent) {
        data = data.filter((tag: any) => tag !== "Free");
        data.push("Free");
      }

      // Add "New" tag if content is new (but not for labs - they get it as a separate squared tag)
      if (
        !data.includes("New") &&
        this.newContent &&
        Array.isArray(data) &&
        this.content?.type !== "lab"
      ) {
        data.push("New");
      }

      // Add "Rev Up to Recert" tag if content is part of a Rev Up campaign
      if (this.isRevup) {
        data = data.filter((tag: any) => tag !== "Rev Up to Recert");
        data = ["Rev Up to Recert"];
      }

      // remove any empty fields
      data = data.filter((tag: any) => tag !== "");

      return data;
    },
    squaredResult(): Array<string> {
      return this.squared ? this.results : [];
    },
    roundedResult(): Array<string> {
      return this.squared ? [] : this.results;
    },
    isPartial(): boolean {
      return this.content?.is_partial === 1 && this.content?.type === "path";
    },
    hasRetiringTag(): string {
      let data = this.content.tags ?? [];
      // ensure that data is an array
      data = Array.isArray(data) ? data : [data];
      if (!data.includes(retiringSoonTxt) && this.isRetiringSoon) {
        return retiringSoonTxt;
      }
      return "";
    },
    isMySubscription(): boolean {
      return (this.userEntitlements ?? []).some(
        (entitlement: any) =>
          this.priceDetails?.productGuid === entitlement?.product_id &&
          entitlement?.end_date &&
          !dayjs().isAfter(dayjs(entitlement?.end_date))
      );
    },
    isPopular(): boolean {
      if (this.content?.contentSection === "featured_popular") return false;
      // Labs should not show the popular tag
      if (this.content?.type === "lab") return false;
      return (this.popularProduct ?? []).includes(
        this.priceDetails?.productGuid
      );
    },
    is90Day(): boolean {
      const subscriptionLength = this.priceDetails?.subscription_length_days;
      if (this.isLabPage && subscriptionLength > 0) return true;
      if (!this.isLabPage && subscriptionLength > 0) return false;
      return true;
    },
    subscriptionDays(): string {
      return this.priceDetails?.subscription_length_days
        ? `${this.priceDetails.subscription_length_days}-day access`
        : "";
    },
    contentNew(): string {
      return this.content && this.newContent ? "New" : "";
    },

    visibleTags() {
      const tags = [];

      if (this.showRevUpRecertCampaign) tags.push("revup");
      if (this.freeContent) tags.push("contentFree");
      if (this.contentNew !== "") tags.push("contentNew");
      if (
        !this.freeContent &&
        this.showSubscriptionTag &&
        !this.isBundleOrExamVoucher
      )
        tags.push("subscription");
      if (this.subscriptionDays) tags.push("days");
      if (this.isPopular) tags.push("popular");
      return tags.slice(0, 3); // max 3 tags
    },
    isBundleOrExamVoucher(): boolean {
      return (
        this.content?.type === "bundle" ||
        this.content?.type === "exam-voucher" ||
        this.content?.type === "lab"
      );
    },
  },
});
</script>

<template>
  <div v-if="isStorePage" class="flex flex-wrap items-center gap-2">
    <cu-tags
      v-if="visibleTags.includes('revup')"
      text="Rev Up to Recert"
      size="s"
      bgColor=""
      color="#0D0D0E"
      style="--bg-color: #d9ebf9"
    />
    <cu-tags
      v-if="visibleTags.includes('contentFree')"
      text="Free"
      size="s"
      bgColor=""
      color="#0D0D0E"
      data-cy="free-store"
      style="--bg-color: #d9ebf9"
    />
    <cu-tags
      v-if="visibleTags.includes('subscription')"
      text="In your subscription"
      size="s"
      data-cy="in-your-subscription-store"
      bgColor="gray-dark"
      color="white"
    />

    <cu-tags
      v-if="visibleTags.includes('days')"
      :text="subscriptionDays"
      size="s"
      data-cy="subscription-days-store"
      bgColor="blue-light-30"
    />

    <cu-tags
      v-if="visibleTags.includes('popular')"
      text="Popular"
      size="s"
      data-cy="popular-product-store"
      bgColor="blue-light-30"
    />

    <cu-tags
      v-if="visibleTags.includes('contentNew')"
      :text="contentNew"
      size="s"
      bgColor=""
      color="#0D0D0E"
      data-cy="new-store"
      style="--bg-color: #d9ebf9"
    />
  </div>
  <div v-else class="flex flex-wrap items-center gap-2">
    <cu-tags
      v-if="showRevUpRecertCampaign"
      text="Rev Up to Recert"
      size="s"
      bgColor=""
      color="#0D0D0E"
      style="--bg-color: #d9ebf9"
    />

    <cu-tags
      v-for="tag in roundedResult"
      :text="tag"
      :key="tag"
      size="s"
      bgColor=""
      color="#0D0D0E"
      style="--bg-color: #d9ebf9"
    />

    <cu-tags
      v-for="tag in squaredResult"
      :text="tag"
      :key="tag"
      size="s"
      bgColor=""
      color="#0D0D0E"
      style="--bg-color: #d9ebf9"
      squared=""
    />
    <cu-tags
      v-if="isRetiringSoon"
      text="Retiring soon"
      size="s"
      bgColor=""
      color="black"
      style="--bg-color: #fee6c0"
    />
    <cu-tags
      v-if="contentRetired !== ''"
      :text="contentRetired"
      size="s"
      bgColor=""
      color="black"
      style="--bg-color: #fee6c0"
    />
    <cu-tags
      v-if="isPartial && !showRevUpRecertCampaign"
      text="Special release"
      size="s"
      bgColor="blue-light"
      ghost=""
    />
    <cu-tags
      v-if="isMySubscription && isStorePage"
      text="In your subscription"
      size="s"
      data-cy="in-your-subscription"
      bgColor="gray-dark"
      color="white"
    />
    <cu-tags
      v-if="subscriptionDays && is90Day"
      :text="subscriptionDays"
      size="s"
      data-cy="subscription-days"
      bgColor="blue-light-30"
      style="--bg-color: #d1e6f7"
      squared=""
    />
    <cu-tags
      v-if="content?.type === 'lab' && contentNew"
      :text="contentNew"
      size="s"
      data-cy="new-lab"
      bgColor=""
      color="#0D0D0E"
      style="--bg-color: #d9ebf9"
      squared=""
    />
    <cu-tags
      v-if="isPopular"
      text="Popular"
      size="s"
      data-cy="popular-product"
      bgColor="blue-light-30"
    />
    <cu-tags
      v-if="inGracePeriod"
      text="Access expired"
      size="s"
      data-cy="access-expired"
      bgColor="orange-30"
      color="#0D0D0E"
      squared=""
    />
  </div>
</template>
